<template>
  <div class="space-y-4">
    <div v-for="(cr, idx) in containerResults" :key="idx" class="mt-card p-4">
      <!-- Question content -->
      <div class="mb-3">
        <ContentRow
          v-for="row in sorted(cr.questionContents)"
          :key="'q-' + row.rownum"
          :row="row"
        />
      </div>

      <!-- Student answer (hidden for skipped questions) -->
      <div v-if="!isSkipped" class="flex items-start gap-2 mb-2">
        <MTIcon
          v-if="showFeedback"
          :name="cr.correct ? 'check-circle' : 'x-circle'"
          :size="18"
          :class="cr.correct ? 'text-green-600' : 'text-red-500'"
          class="mt-0.5 shrink-0"
        />
        <div>
          <span class="text-sm font-medium">Your answer: </span>
          <span class="text-sm" :class="showFeedback ? (cr.correct ? 'text-green-500' : 'text-red-500') : ''">
            {{ formatAnswer(cr.studentAnswer, cr.answerDatatype, cr.options) }}
          </span>
        </div>
      </div>

      <!-- Correct answer — shown if wrong in feedback mode, or always for skipped -->
      <div v-if="(showFeedback && !cr.correct) || isSkipped" class="flex items-start gap-2 mb-2" :class="isSkipped ? '' : 'ml-6'">
        <span class="text-sm font-medium text-[var(--mt-text-muted)]">Correct answer: </span>
        <span class="text-sm text-green-500 font-medium">
          {{ formatAnswer(cr.correctAnswer, cr.answerDatatype, cr.options) }}
        </span>
      </div>

      <!-- Subtopic link — shown in feedback mode or for skipped questions -->
      <div v-if="(showFeedback || isSkipped) && subtopicInfo" class="mt-2">
        <router-link
          :to="`/student/${studentId}/learn/${subtopicInfo.lessonId}/${sectionGroupId}`"
          class="text-xs flex items-center gap-1 review-link"
        >
          <MTIcon name="book-open" :size="14" />
          {{ subtopicInfo.subtopicTitle || sectionGroupId }}
        </router-link>
      </div>

      <!-- Expandable solution — shown in feedback mode or for skipped questions -->
      <div v-if="(showFeedback || isSkipped) && cr.solutionContents?.length" class="mt-3">
        <button
          class="text-xs font-medium flex items-center gap-1 review-link"
          @click="toggleSolution(idx)"
        >
          <MTIcon :name="expandedSolutions[idx] ? 'chevron-down' : 'chevron-right'" :size="14" />
          {{ expandedSolutions[idx] ? 'Hide solution' : 'Show solution' }}
        </button>
        <div v-if="expandedSolutions[idx]" class="mt-2 pl-4 border-l-2 border-[var(--mt-border)]">
          <ContentRow
            v-for="row in sorted(cr.solutionContents)"
            :key="'s-' + row.rownum"
            :row="row"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed } from "vue";
import ContentRow from "@/components/questions/ContentRow.vue";
import MTIcon from "@/components/ui/MTIcon.vue";
import { useCurriculumStore } from "@/stores/curriculumStore";

const props = defineProps({
  containerResults: { type: Array, required: true },
  sectionGroupId: { type: String, default: "" },
  studentId: { type: String, default: "" },
  showFeedback: { type: Boolean, default: true },
  isSkipped: { type: Boolean, default: false }
});

const curriculumStore = useCurriculumStore();
const expandedSolutions = reactive({});

const subtopicInfo = computed(() => {
  if (!props.sectionGroupId) return null;
  return findLessonForSG(props.sectionGroupId);
});

function findLessonForSG(sgId) {
  for (const mod of curriculumStore.modules) {
    for (const unit of mod.units) {
      for (const lesson of unit.lessons) {
        for (const sub of lesson.subsections || []) {
          if (sub.content === sgId || sub.name === sgId) {
            return {
              lessonId: lesson.id,
              lessonTitle: lesson.title,
              subtopicTitle: sub.title || sgId
            };
          }
        }
      }
    }
  }
  return null;
}

function toggleSolution(idx) {
  expandedSolutions[idx] = !expandedSolutions[idx];
}

function sorted(arr) {
  return (arr || []).slice().sort((a, b) => Number(a.rownum) - Number(b.rownum));
}

function formatAnswer(answer, datatype, options) {
  if (answer === null || answer === undefined) return "—";

  if (datatype === "BOOLEAN") {
    return answer === true || answer === "TRUE" || answer === "true" ? "Yes" : "No";
  }

  if (datatype === "OPTION" && options?.length) {
    const opt = options.find(o => o.optionValue === answer);
    if (opt) {
      // Use rowContent text if available, otherwise option value
      return opt.rowContent || answer;
    }
  }

  return String(answer);
}
</script>

<style scoped>
.review-link {
  color: var(--mt-accent-blue);
}
.review-link:hover {
  opacity: 0.8;
}
</style>
